import { useState, useEffect, useCallback } from 'react';
import { WifiOff, Cloud, CloudOff, Loader2 } from 'lucide-react';
import { offlineQueue } from '@/utils/offlineQueue';
import { cn } from '@/lib/utils';

export const OfflineIndicator = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'complete'>('idle');
  const [pendingCount, setPendingCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  // Debounce visibility changes to avoid flickering
  const updateVisibility = useCallback((shouldShow: boolean) => {
    if (shouldShow) {
      setIsVisible(true);
    } else {
      // Delay hiding to avoid flickering
      setTimeout(() => setIsVisible(false), 300);
    }
  }, []);

  useEffect(() => {
    // Initialize pending count after mount to avoid hydration issues
    setPendingCount(offlineQueue.getQueueLength());

    const handleOnline = () => {
      setIsOnline(true);
      // Proactively trigger sync (browser 'online' event can be delayed)
      offlineQueue.syncQueue();
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Re-check on focus/visibility — covers cases where 'online' didn't fire
    const recheck = () => {
      if (navigator.onLine) {
        setIsOnline(true);
        offlineQueue.syncQueue();
      } else {
        setIsOnline(false);
      }
    };
    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', recheck);

    const unsubscribe = offlineQueue.subscribe((status) => {
      if (status === 'syncing') {
        setSyncStatus('syncing');
        // Refresh pending count immediately so UI reflects in-progress sync
        setPendingCount(offlineQueue.getQueueLength());
      } else if (status === 'complete') {
        setSyncStatus('complete');
        setPendingCount(offlineQueue.getQueueLength());
        
        // Reset to idle after showing complete
        setTimeout(() => setSyncStatus('idle'), 2000);
      } else if (status === 'online') {
        // Network came back — refresh count immediately, sync will kick in
        setPendingCount(offlineQueue.getQueueLength());
      }
    });

    // Lightweight poll as a safety net (queue subscribe handles most updates)
    const interval = setInterval(() => {
      setPendingCount(offlineQueue.getQueueLength());
    }, 3000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('focus', recheck);
      document.removeEventListener('visibilitychange', recheck);
      unsubscribe();
      clearInterval(interval);
    };
  }, []);

  // Update visibility based on state
  useEffect(() => {
    const shouldShow = !isOnline || syncStatus !== 'idle' || pendingCount > 0;
    updateVisibility(shouldShow);
  }, [isOnline, syncStatus, pendingCount, updateVisibility]);

  // Don't render anything if not visible
  if (!isVisible && isOnline && pendingCount === 0 && syncStatus === 'idle') {
    return null;
  }

  return (
    <div
      className={cn(
        'fixed left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-full shadow-lg flex items-center gap-2 text-sm font-medium transition-all duration-300 bottom-[calc(5rem+env(safe-area-inset-bottom,0px))]',
        !isVisible && 'opacity-0 pointer-events-none',
        isVisible && 'opacity-100',
        !isOnline && 'bg-destructive text-destructive-foreground',
        isOnline && syncStatus === 'syncing' && 'bg-primary text-primary-foreground',
        isOnline && syncStatus === 'complete' && 'bg-green-500 text-white',
        isOnline && pendingCount > 0 && syncStatus === 'idle' && 'bg-amber-500 text-white'
      )}
      role="status"
      aria-live="polite"
    >
      {!isOnline && (
        <>
          <WifiOff className="w-4 h-4" aria-hidden="true" />
          <span>Offline</span>
          {pendingCount > 0 && (
            <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">
              {pendingCount} pending
            </span>
          )}
        </>
      )}

      {isOnline && syncStatus === 'syncing' && (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Syncing...</span>
        </>
      )}

      {isOnline && syncStatus === 'complete' && (
        <>
          <Cloud className="w-4 h-4" />
          <span>Synced</span>
        </>
      )}

      {isOnline && pendingCount > 0 && syncStatus === 'idle' && (
        <>
          <CloudOff className="w-4 h-4" />
          <span>{pendingCount} pending</span>
        </>
      )}
    </div>
  );
};
